import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Td,
  Tr,
} from "@chakra-ui/react";
import { SetStateAction, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCheck,
  FaEllipsisV,
  FaFileDownload,
  FaFilePdf,
  FaPlus,
  FaSignature,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";
import { DocumentCategory, ViewDocumentAsOwnerDto } from "../../autogen/bff-api";
import { DOCUMENT_CATEGORY_ORDER, useDocumentCategoryLabel } from "./useDocumentCategoryLabel";
import { requireBooleanEnvVar } from "../../config";
import { MainDocumentContext } from "../../pages/contracts/edit/docs/signatures/CreateSignedDocument";
import { displayDateWithTime } from "../formatting/displayDateWithTime";
import { Logo } from "../Logo";
import { Explanation } from "../support/Explanation/Explanation";
import { isAiAnalysable } from "./CombinedDocuments";
import { useDocumentDownloader } from "./DownloadDocumentIconButton/useDocumentDownloader";
import { isPdf, isSignable, isViewDocumentAsOwnerDto } from "./utils";

export const ViewUploadedDocumentRow = ({
  doc,
  canEditName,
  canEditTags,
  canEditCategory = false,
  onUpdateDocumentMeta,
  onUpdateCategory,
  isAiChatEnabled,
  setDocumentForAnalysis,
  setDocumentForFencyModal,
  setDocumentToSign,
  canDeleteDocument,
  deleteDocument,
  isTagsColumnVisible,
  isCategoryColumnVisible = false,
  isCreatedByColumnVisible,
  setPreviewPdfUrl,
}: {
  doc: ViewDocumentAsOwnerDto;
  canEditName: boolean;
  canEditTags: boolean;
  canEditCategory?: boolean;
  onUpdateDocumentMeta: ({
    document,
    name,
    tags,
  }: {
    document: ViewDocumentAsOwnerDto;
    name: string;
    tags: string[];
  }) => void;
  onUpdateCategory?: (docId: string, category: DocumentCategory | null) => void;
  isAiChatEnabled: boolean;
  setDocumentForAnalysis: (doc: ViewDocumentAsOwnerDto) => void;
  setDocumentForFencyModal: (doc: ViewDocumentAsOwnerDto) => void;
  setDocumentToSign?: (value: SetStateAction<MainDocumentContext | undefined>) => void;
  canDeleteDocument: boolean;
  deleteDocument: (document: ViewDocumentAsOwnerDto) => Promise<void>;
  isTagsColumnVisible: boolean;
  isCategoryColumnVisible?: boolean;
  isCreatedByColumnVisible: boolean;
  setPreviewPdfUrl: (url: string) => void;
}) => {
  const { t } = useTranslation();
  const documentCategoryLabel = useDocumentCategoryLabel();
  const fencyDocumentChatEnabled = requireBooleanEnvVar("VITE_ENABLE_FENCY_DOCUMENT_CHAT");

  const { getDownloadLink } = useDocumentDownloader();
  const [isCreatingDownloadLink, setIsCreatingDownloadLink] = useState<boolean>(false);
  const [isCreatingPreviewLink, setIsCreatingPreviewLink] = useState<boolean>(false);

  const [newTag, setNewTag] = useState<string>();
  const [showConfirmDeletion, setShowConfirmDeletion] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);
  const deleteInFlight = useRef(false);

  return (
    <Tr>
      <Td px="3">
        <Input
          size="sm"
          rounded="md"
          fontWeight="normal"
          minWidth={{ base: "60", lg: "80" }}
          value={doc.name}
          _focus={{
            borderColor: "default",
            boxShadow: "default",
          }}
          _disabled={{ border: "none" }}
          isDisabled={!canEditName}
          onChange={(e) =>
            isViewDocumentAsOwnerDto(doc)
              ? onUpdateDocumentMeta({ document: doc, name: e.target.value, tags: doc.tags })
              : undefined
          }
        />
      </Td>
      <Td fontSize="sm">{displayDateWithTime(doc.uploadedAt)}</Td>
      {isCreatedByColumnVisible && (
        <Td fontSize="sm">
          {isViewDocumentAsOwnerDto(doc) ? `${doc.uploadedBy?.firstName} ${doc.uploadedBy?.lastName}` : ""}
        </Td>
      )}
      {isTagsColumnVisible && (
        <Td>
          {isViewDocumentAsOwnerDto(doc) && (
            <Flex flexWrap="wrap" columnGap="1" rowGap="1" width="48" maxWidth="48">
              {doc.tags.map((tag, i) => (
                <Tag
                  key={`${tag}-${i}`}
                  size="sm"
                  backgroundColor="smSecondarySoft"
                  onClick={(e) => e.stopPropagation()}
                >
                  <TagLabel>{tag}</TagLabel>
                  {canEditTags && (
                    <TagCloseButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateDocumentMeta({
                          document: doc,
                          name: doc.name,
                          tags: doc.tags.filter((_, j) => i !== j),
                        });
                        setNewTag(undefined);
                      }}
                    />
                  )}
                </Tag>
              ))}
              {canEditTags && newTag === undefined && (
                <IconButton
                  aria-label="add tag"
                  variant="ghost"
                  size="xs"
                  colorScheme="cyan"
                  icon={<Icon as={FaPlus} />}
                  onClick={() => setNewTag("")}
                />
              )}
              {newTag !== undefined && (
                <InputGroup display="inline-flex" width="40">
                  <Input
                    size="xs"
                    rounded="md"
                    pr="6"
                    autoFocus={true}
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                  />
                  <InputRightElement width="12" height="6">
                    <IconButton
                      height="4"
                      size="xs"
                      aria-label="save tag"
                      icon={<Icon as={FaTimes} />}
                      rounded="none"
                      variant="ghost"
                      color="smMuted"
                      p="0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewTag(undefined);
                      }}
                    />
                    <IconButton
                      size="xs"
                      aria-label="save tag"
                      icon={<Icon as={FaCheck} />}
                      roundedLeft="none"
                      colorScheme="teal"
                      onClick={async () => {
                        onUpdateDocumentMeta({ document: doc, name: doc.name, tags: [...(doc.tags ?? []), newTag] });
                        setNewTag(undefined);
                      }}
                    />
                  </InputRightElement>
                </InputGroup>
              )}
            </Flex>
          )}
        </Td>
      )}
      {isCategoryColumnVisible && isViewDocumentAsOwnerDto(doc) && (
        <Td>
          {canEditCategory ? (
            <Select
              size="sm"
              rounded="md"
              value={doc.category ?? ""}
              placeholder={t("Select category") ?? ""}
              aria-label={t("document category") ?? "document category"}
              onChange={(e) => {
                const next = e.target.value === "" ? null : (e.target.value as DocumentCategory);
                onUpdateCategory?.(doc.id, next);
              }}
            >
              {DOCUMENT_CATEGORY_ORDER.map((cat) => (
                <option key={cat} value={cat}>
                  {documentCategoryLabel(cat)}
                </option>
              ))}
            </Select>
          ) : doc.category ? (
            documentCategoryLabel(doc.category)
          ) : (
            "-"
          )}
        </Td>
      )}
      {isCategoryColumnVisible && !isViewDocumentAsOwnerDto(doc) && <Td />}
      <Td>
        <Menu
          isOpen={isActionMenuOpen}
          onOpen={() => setIsActionMenuOpen(true)}
          onClose={() => {
            setIsActionMenuOpen(false);
            setShowConfirmDeletion(false);
          }}
          closeOnSelect={false}
        >
          <MenuButton
            aria-label="actions"
            as={IconButton}
            size="sm"
            variant="ghost"
            icon={<Icon as={FaEllipsisV} />}
            onClick={(e) => e.stopPropagation()}
          />
          <MenuList>
            {isPdf(doc) && (
              <MenuItem
                icon={<Icon as={FaFilePdf} />}
                onClick={async (e) => {
                  e.stopPropagation();
                  setIsCreatingPreviewLink(true);
                  const link = await getDownloadLink(doc.id, false).catch((e) => {
                    setIsCreatingPreviewLink(false);
                    throw e;
                  });
                  setIsCreatingPreviewLink(false);
                  if (link) setPreviewPdfUrl(link);
                  setIsActionMenuOpen(false);
                }}
              >
                {t("Preview")} {isCreatingPreviewLink && <Spinner size="sm" ml="2" />}
              </MenuItem>
            )}
            <MenuItem
              icon={<Icon as={FaFileDownload} />}
              onClick={async (e) => {
                e.stopPropagation();
                setIsCreatingDownloadLink(true);
                const link = await getDownloadLink(doc.id, false).catch((e) => {
                  setIsCreatingDownloadLink(false);
                  throw e;
                });
                setIsCreatingDownloadLink(false);
                if (link) window.open(link, "_blank", "noopener,noreferrer");
                setIsActionMenuOpen(false);
              }}
            >
              {t("Download")} {isCreatingDownloadLink && <Spinner size="sm" ml="2" />}
            </MenuItem>
            {setDocumentToSign && (
              <MenuItem
                aria-label="sign document"
                icon={<Icon as={FaSignature} />}
                isDisabled={!isSignable(doc)}
                onClick={(e) => {
                  e.stopPropagation();
                  setDocumentToSign({ documentId: doc.id });
                  setIsActionMenuOpen(false);
                }}
              >
                {t("Sign document")}
              </MenuItem>
            )}
            {canDeleteDocument && !showConfirmDeletion && (
              <MenuItem
                icon={<Icon as={FaTrashAlt} color="smDanger" />}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowConfirmDeletion(true);
                }}
              >
                {t("Delete document")}
              </MenuItem>
            )}
            {showConfirmDeletion && (
              <MenuItem
                as={Button}
                size="sm"
                variant="unstyled"
                color="smBackground"
                backgroundColor="smDanger"
                fontWeight="normal"
                icon={<Icon as={FaTrashAlt} color="smBackground" />}
                rightIcon={
                  <Icon
                    as={FaTimes}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowConfirmDeletion(false);
                    }}
                  />
                }
                isDisabled={isDeletingDocument}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!isViewDocumentAsOwnerDto(doc) || deleteInFlight.current) return;
                  deleteInFlight.current = true;
                  setIsDeletingDocument(true);
                  try {
                    await deleteDocument(doc);
                  } finally {
                    deleteInFlight.current = false;
                    setIsDeletingDocument(false);
                  }
                  setIsActionMenuOpen(false);
                }}
              >
                {t("Confirm")} {isDeletingDocument && <Spinner size="sm" color="smBackground" ml="2" />}
              </MenuItem>
            )}
            {isAiChatEnabled && (
              <>
                <Box as="span" display="block">
                  <Explanation
                    text={
                      isViewDocumentAsOwnerDto(doc) && isAiAnalysable(doc)
                        ? t("Chat with document")
                        : t("Can only chat with PDFs")
                    }
                    shouldWrapChildren
                  >
                    <MenuItem
                      isDisabled={!isViewDocumentAsOwnerDto(doc) || !isAiAnalysable(doc)}
                      aria-label="analyze with AI"
                      color="smPrimary"
                      icon={<Logo />}
                      onClick={() => isViewDocumentAsOwnerDto(doc) && setDocumentForAnalysis(doc)}
                    >
                      {t("Chat with document")}
                    </MenuItem>
                  </Explanation>
                </Box>
                {fencyDocumentChatEnabled && (
                  <Box as="span" display="block">
                    <Explanation
                      text={
                        isViewDocumentAsOwnerDto(doc) && isAiAnalysable(doc)
                          ? t("Chat with document (Fency)")
                          : t("Can only chat with PDFs")
                      }
                      shouldWrapChildren
                    >
                      <MenuItem
                        isDisabled={!isViewDocumentAsOwnerDto(doc) || !isAiAnalysable(doc)}
                        aria-label="chat with document Fency"
                        color="smPrimary"
                        icon={<Logo />}
                        onClick={() => {
                          if (isViewDocumentAsOwnerDto(doc)) {
                            setDocumentForFencyModal(doc);
                            setIsActionMenuOpen(false);
                          }
                        }}
                      >
                        {t("Chat with document (Fency)")}
                      </MenuItem>
                    </Explanation>
                  </Box>
                )}
              </>
            )}
          </MenuList>
        </Menu>
      </Td>
    </Tr>
  );
};
